import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { TableListItem } from '@command-floor/shared-types';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

type SortCol = 'status' | 'name' | 'entity' | 'category';
type SortDir = 'asc' | 'desc';
type StatusFilter = 'ALL' | 'RED' | 'YELLOW' | 'GREY';

const FLAG_DOT: Record<string, string> = {
  RED: 'bg-flag-red',
  YELLOW: 'bg-flag-yellow',
  GREY: 'bg-flag-grey',
};

const FLAG_LABEL: Record<string, string> = {
  RED: 'Stopped',
  YELLOW: 'Help needed',
  GREY: 'On track',
};

const FLAG_PRIORITY: Record<string, number> = { RED: 0, YELLOW: 1, GREY: 2 };

function flagSortVal(t: TableListItem) {
  return FLAG_PRIORITY[t.status_flag] ?? 3;
}

function IconFilter() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    >
      <line x1="2" y1="5" x2="14" y2="5" />
      <line x1="4" y1="9" x2="12" y2="9" />
      <line x1="6" y1="13" x2="10" y2="13" />
    </svg>
  );
}

function SortTh({
  col,
  current,
  onClick,
  children,
  className = '',
}: {
  col: SortCol;
  current: { col: SortCol; dir: SortDir };
  onClick: (col: SortCol) => void;
  children: React.ReactNode;
  className?: string;
}) {
  const active = current.col === col;
  return (
    <th
      onClick={() => onClick(col)}
      className={`cursor-pointer select-none py-2.5 text-left text-xs font-semibold uppercase tracking-wider transition-colors hover:text-slate-300 ${
        active ? 'text-slate-200' : 'text-slate-500'
      } ${className}`}
    >
      {children}
      {active && <span className="ml-1 opacity-70">{current.dir === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );
}

export function TableListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [sort, setSort] = useState<{ col: SortCol; dir: SortDir }>({ col: 'status', dir: 'asc' });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [entityFilter, setEntityFilter] = useState<string[]>([]);

  const { data: tables = [], isLoading } = useQuery({
    queryKey: ['tables-list'],
    queryFn: () => api.get<TableListItem[]>('/api/tables'),
  });

  const blueTable = tables.find((t) => t.is_blue_for_viewer) ?? null;
  const canReview = user?.role === 'LEADERSHIP' || user?.role === 'ENTITY_MANAGER';

  const entities = useMemo(() => {
    const map = new Map<string, string>();
    tables.forEach((t) => map.set(t.entity_id, t.entity_name));
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tables]);

  const displayed = useMemo(() => {
    let result = [...tables];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.entity_name.toLowerCase().includes(q) ||
          t.category_name.toLowerCase().includes(q),
      );
    }
    if (statusFilter !== 'ALL') result = result.filter((t) => t.status_flag === statusFilter);
    if (entityFilter.length) result = result.filter((t) => entityFilter.includes(t.entity_id));

    result.sort((a, b) => {
      let cmp = 0;
      switch (sort.col) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'entity':
          cmp = a.entity_name.localeCompare(b.entity_name) || a.name.localeCompare(b.name);
          break;
        case 'category':
          cmp = a.category_name.localeCompare(b.category_name) || a.name.localeCompare(b.name);
          break;
        case 'status':
          cmp = flagSortVal(a) - flagSortVal(b) || a.name.localeCompare(b.name);
          break;
      }
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [tables, search, statusFilter, entityFilter, sort]);

  function toggleSort(col: SortCol) {
    setSort((s) =>
      s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' },
    );
  }

  function toggleEntity(id: string) {
    setEntityFilter((prev) => (prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]));
  }

  const hasFilters = statusFilter !== 'ALL' || entityFilter.length > 0 || search !== '';
  const activeFilterCount = entityFilter.length + (statusFilter !== 'ALL' ? 1 : 0);

  return (
    <div className="relative flex h-full overflow-hidden">
      {/* ── Main content ── */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-none border-b border-aurora bg-surface-panel px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-slate-100">Work Efforts</h1>
              <p className="text-xs text-slate-500">{tables.length} total</p>
            </div>
            <button
              onClick={() => setDrawerOpen((o) => !o)}
              className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                drawerOpen || activeFilterCount > 0
                  ? 'border-pyek-cyan text-pyek-cyan'
                  : 'border-aurora text-slate-400 hover:text-slate-200'
              }`}
            >
              <IconFilter />
              Filters
              {activeFilterCount > 0 && (
                <span className="rounded-full bg-pyek-cyan/20 px-1.5 py-0.5 text-xs text-pyek-cyan">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          {/* Status pills */}
          <div className="mt-3 flex flex-wrap gap-2">
            {(['ALL', 'RED', 'YELLOW', 'GREY'] as StatusFilter[]).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs transition-colors ${
                  statusFilter === s
                    ? 'bg-slate-700 font-medium text-slate-100'
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                {s !== 'ALL' && <span className={`h-2 w-2 rounded-sm ${FLAG_DOT[s]}`} />}
                {s === 'ALL' ? 'All statuses' : FLAG_LABEL[s]}
              </button>
            ))}
          </div>

          {/* Search */}
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, entity, or category…"
            className="mt-3 w-full rounded-lg border border-aurora px-3 py-2 text-sm text-slate-200 placeholder-slate-500"
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Next Up for Review */}
          {canReview && blueTable && (
            <button
              onClick={() => navigate(`/tables/${blueTable.id}`)}
              className="flex w-full items-center gap-3 border-b border-aurora bg-pyek-navy/40 px-6 py-3 text-left transition-colors hover:bg-pyek-navy/60"
            >
              <span className="shrink-0 text-xs font-semibold uppercase tracking-wider text-pyek-cyan">
                ★ Next Up for Review
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-100">
                {blueTable.name}
              </span>
              <span className="shrink-0 truncate text-xs text-slate-400">
                {blueTable.entity_name} › {blueTable.category_name}
              </span>
              <span className="ml-2 shrink-0 text-slate-500">→</span>
            </button>
          )}

          {isLoading && <p className="px-6 py-4 text-sm text-slate-500">Loading…</p>}

          {!isLoading && displayed.length === 0 && (
            <p className="px-6 py-4 text-sm italic text-slate-500">
              {hasFilters ? 'No results match your filters.' : 'No accessible work efforts.'}
            </p>
          )}

          {!isLoading && displayed.length > 0 && (
            <table className="w-full text-sm">
              <thead className="sticky top-0 border-b border-aurora bg-surface-panel">
                <tr>
                  <SortTh col="status" current={sort} onClick={toggleSort} className="w-36 pl-6">
                    Status
                  </SortTh>
                  <SortTh col="name" current={sort} onClick={toggleSort}>
                    Name
                  </SortTh>
                  <SortTh col="entity" current={sort} onClick={toggleSort} className="w-44">
                    Entity
                  </SortTh>
                  <SortTh col="category" current={sort} onClick={toggleSort} className="w-52 pr-6">
                    Category
                  </SortTh>
                </tr>
              </thead>
              <tbody>
                {displayed.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => navigate(`/tables/${t.id}`)}
                    className="cursor-pointer border-b border-aurora/40 transition-colors hover:bg-white/5"
                  >
                    <td className="py-3 pl-6">
                      <div className="flex items-center gap-2">
                        <span
                          className={`h-2.5 w-2.5 shrink-0 rounded-sm ${FLAG_DOT[t.status_flag]}`}
                        />
                        <span className="text-xs text-slate-500">{FLAG_LABEL[t.status_flag]}</span>
                      </div>
                    </td>
                    <td className="py-3 font-medium text-slate-200">{t.name}</td>
                    <td className="py-3 text-slate-400">{t.entity_name}</td>
                    <td className="py-3 pr-6 text-slate-400">
                      {t.category_name}
                      {t.subcategory_name ? ` › ${t.subcategory_name}` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Filter drawer ── */}
      {drawerOpen && (
        <>
          {/* Backdrop */}
          <div className="absolute inset-0 z-10" onClick={() => setDrawerOpen(false)} />
          {/* Panel */}
          <div className="absolute right-0 top-0 z-20 flex h-full w-64 flex-col border-l border-aurora bg-surface-panel shadow-2xl">
            <div className="flex items-center justify-between border-b border-aurora px-4 py-3">
              <span className="text-sm font-medium text-slate-200">Filters</span>
              {activeFilterCount > 0 && (
                <button
                  onClick={() => {
                    setEntityFilter([]);
                    setStatusFilter('ALL');
                  }}
                  className="text-xs text-slate-500 hover:text-slate-300"
                >
                  Clear all
                </button>
              )}
              <button
                onClick={() => setDrawerOpen(false)}
                className="ml-2 flex h-6 w-6 items-center justify-center rounded text-slate-500 hover:bg-white/5 hover:text-slate-300"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              {/* Entity filter */}
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Entity
              </p>
              <div className="space-y-2">
                {entities.map((e) => (
                  <label
                    key={e.id}
                    className="flex cursor-pointer items-center gap-2 text-sm text-slate-300 hover:text-slate-100"
                  >
                    <input
                      type="checkbox"
                      checked={entityFilter.includes(e.id)}
                      onChange={() => toggleEntity(e.id)}
                      className="h-3.5 w-3.5 rounded accent-pyek-cyan"
                    />
                    {e.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
